import React from 'react';
import { useSelector } from 'react-redux';
import classNames from 'classnames';
import {
  FieldArrayRenderProps,
  useField,
} from 'formik';
import * as selectors from 'hellospa/page/prep-and-send/data/selectors';
import * as types from 'hellospa/page/prep-and-send/data/types';
import { DragHandle } from 'hellospa/components/drag-sort';
import styles from './index.scss';
import Fax from './fax';
import Signer from './signer';
import Role from './role';
import ButtonsContainer from './buttons-container';
import RecipientDropdown from './recipient-dropdown';
import AuthenticationFields from './authentication-fields';

type Props = {
  recipient: types.Recipient,
  index: number,
  formikHelpers: FieldArrayRenderProps,
};

type State = {
  isAuthFieldsVisible: boolean,
}

const Row = (props: Props) => {
  const {
    recipient,
    index,
    formikHelpers,
  } = props;

  const parent = `recipients.${index}`;

  const [recipientsField] = useField<types.Recipient[]>('recipients');
  const [recipientOrderField, , recipientOrderHelper] = useField<Boolean>('recipientOrder');

  const isSendingTemplate = useSelector(selectors.isSendingTemplate);
  const allowDelete = (
    useSelector(selectors.allowRecipientDelete)
    && recipientsField.value.length > 1
  );
  const [, nameMeta] = useField<String>(`${parent}.name`);
  const [, emailMeta] = useField<String>(`${parent}.email`);
  const [, smsDeliveryMeta] = useField<String>(`${parent}.smsDeliveryMobileNumber`);
  const [, accessCodeMeta, accessCodeHelpers] = useField<String>(`${parent}.accessCode`);
  const [, smsAuthNumberMeta, smsAuthNumberHelpers] = useField<String>(`${parent}.smsAuthNumber`);
  const [, , authTypeHelpers] = useField<String>(`${parent}.authType`);

  const allowSmsAuth = useSelector(selectors.allowSmsAuth)
    && recipient.type === types.RecipientTypes.Signer;

  const isDeepIntegration = useSelector(selectors.isDeepIntegration);

  const [state, setState] = React.useState<State>({
    isAuthFieldsVisible: !!(accessCodeMeta.initialValue || smsAuthNumberMeta.initialValue)
      && allowSmsAuth
      && !isDeepIntegration,
  });

  const remove = () => {
    if (allowDelete) {
      // Disable signer order checkbox if we're going down to a single signer
      if (recipientsField.value.length <= 2) {
        recipientOrderHelper.setValue(false);
      }

      formikHelpers.remove(index);
    }
  };

  const hideRecipientOrder = !recipientOrderField.value
    || isSendingTemplate
    || recipientsField.value.length < 2;

  const showDeleteBtn = allowDelete && recipientsField.value.length > 1;
  // Align the the attachment & access code buttons when row has error.
  const onRowError = (nameMeta.touched && nameMeta.error)
    || (emailMeta.touched && emailMeta.error)
    || (smsDeliveryMeta.touched && smsDeliveryMeta.error)
    || (accessCodeMeta.touched && accessCodeMeta.error)
    || (smsAuthNumberMeta.touched && smsAuthNumberMeta.error);

  const hasExistingAuth = allowSmsAuth
    && !isDeepIntegration
    && (accessCodeMeta.initialValue || smsAuthNumberMeta.initialValue);

  const showAuthFields = () => {
    setState({
      isAuthFieldsVisible: true,
    });
  };

  const removeSignerAuth = () => {
    authTypeHelpers.setValue('');
    accessCodeHelpers.setValue('');
    smsAuthNumberHelpers.setValue('');

    setState({
      isAuthFieldsVisible: false,
    });
  };

  let recipientOptions: JSX.Element;
  if (!isDeepIntegration) {
    recipientOptions = (
      <RecipientDropdown
        recipient={recipient}
        index={index}
        showAuthFields={showAuthFields}
        showDeleteBtn={showDeleteBtn}
        remove={remove} />
    );
  } else {
    recipientOptions = (
      <ButtonsContainer
        index={index}
        recipient={recipient}
        showDeleteBtn={showDeleteBtn}
        allowDelete={allowDelete}
        remove={remove} />
    );
  }

  return (
    <div
      id={recipient.id}
      className={classNames(styles.recipientRow, {
        [styles.onRowError]: onRowError,
      })}
      data-qa-ref="recipients.row"
    >
      {/* Always include order container even if not set, so fields don't jump */}
      <DragHandle
        className={styles.order}
        hidden={hideRecipientOrder}
        data-qa-ref={`recipients.${index}.order`} />

      {recipient.type === types.RecipientTypes.Signer && (
        <Signer index={index} recipient={recipient} />
      )}

      {recipient.type === types.RecipientTypes.Role && (
        <Role index={index} />
      )}

      {recipient.type === types.RecipientTypes.Fax && (
        <Fax index={index} />
      )}

      <div
        data-qa-ref={`recipients.${index}.options`}
        className={classNames(styles.recipientOptionsContainer, {
          [styles.dropdownBtmMargin]: onRowError,
        })}
      >
        {recipientOptions}
      </div>

      {(state.isAuthFieldsVisible || hasExistingAuth) && (
        <AuthenticationFields
          index={index}
          recipient={recipient}
          onClose={removeSignerAuth} />
      )}
    </div>
  );
};

export default Row;
